import { toast } from '@palmetto/palmetto-components';
import { skipToken } from '@reduxjs/toolkit/query';
import { useFlags } from 'launchdarkly-react-client-sdk';
import React, { useCallback, useState } from 'react';

import {
  Flag,
  FlagPayload,
  FlagPurposeType,
  FlagStatus,
  NON_SPECIAL_PACKAGE_REVIEWED_STATUSES,
  QueueDocument,
  QueueHistory,
  QueueHistoryAction,
  QueueStatus,
  RequirementFlagSettingsType,
} from 'types';

import {
  useAddFlagToQueueMutation,
  useGetQueueByIdQuery,
  useResolveFlagMutation,
  useUpdateFlagMutation,
} from '../../services/queues';
import { useAuth } from '../auth/authProvider';
import { isPalmettoFinanceUser } from '../auth/RequirePalmettoFinanceUser';

import { AddFlagModal } from './AddFlagModal';
import ResolveFlagModal from './ResolveFlagModal';
import { NewAddFlagModal } from './RevampAddFlagModal';

export interface QueueFlagsContextValue {
  isModalShowing: boolean;
  flags: Flag[];
  shouldHide: boolean;
  showModal: (
    dataKey: string,
    flagType?: RequirementFlagSettingsType,
    fileName?: string,
    baseCategory?: string,
    type?: string,
  ) => void;
  showEditModal: (flag: Flag) => void;
  showResolveModal: (dataKey: string, fileName?: string, id?: string) => void;
  hideModal: () => void;
  addFlag: ({
    dataKey,
    fileName,
    note,
    reason,
    fileUpload,
    flagType,
    type,
    purposeType,
  }: FlagPayload) => Promise<void>;
  updateFlag: (flagId: string, flag: FlagPayload) => Promise<void>;
  resolveFlag: (dataKey: string, status: string, fileName?: string, id?: string) => Promise<void>;
  getFlag: (dataKey: string | string[], fileName?: string, includeResolved?: boolean) => Flag | undefined;
  getFlagsByDocumentType: (type: string) => Flag[] | undefined;
  getFlagsByFlagType: (
    flagType: RequirementFlagSettingsType,
    includeResolved?: boolean,
    dataKey?: string | string[],
    fileName?: string,
  ) => Flag[] | undefined;
  getAllFlags: (includeResolved?: boolean) => Flag[];
  isLoading: boolean;
  setLoading: (loading: boolean) => void;
  queue?: QueueDocument;
  getVisibleFlags: (flags: Flag[]) => Flag[];
}

export interface QueueFlagsProviderProps {
  children?: React.ReactNode;
  queueId: string;
}

const QueueFlagsContext = React.createContext<QueueFlagsContextValue>({
  isModalShowing: false,
  flags: [],
  shouldHide: true,
  showModal: () => {},
  showEditModal: () => {},
  showResolveModal: () => {},
  hideModal: () => {},
  addFlag: async () => {},
  updateFlag: async () => {},
  resolveFlag: async () => {},
  getFlag: () => undefined,
  getFlagsByDocumentType: () => undefined,
  getFlagsByFlagType: () => undefined,
  getAllFlags: () => [],
  isLoading: false,
  setLoading: () => {},
  queue: undefined,
  getVisibleFlags: (flags) => flags,
});
const { Provider } = QueueFlagsContext;

const QueueFlagsProvider = ({ children, queueId }: QueueFlagsProviderProps) => {
  const { flagDrivenPackageReview = false } = useFlags();
  const [isModalShowing, setIsModalShowing] = React.useState(false);
  const [saveFlag] = useAddFlagToQueueMutation();
  const [updateCurrentFlag] = useUpdateFlagMutation();
  const [resolveCurrentFlag] = useResolveFlagMutation();
  const { data: queue } = useGetQueueByIdQuery(queueId ? { id: queueId } : skipToken);
  const [isResolveModalShowing, setIsResolveModalShowing] = useState(false);
  const { claims } = useAuth();
  const isFinCoUser = isPalmettoFinanceUser(claims);
  const [isLoading, setLoading] = useState<boolean>(false);
  const [isEditMode, setIsEditMode] = useState<boolean>(false);

  const [flagTarget, setFlagTarget] = React.useState<{
    dataKey: string;
    fileName: string;
    baseCategory?: string;
    type?: string;
    flagType?: RequirementFlagSettingsType;
    id?: string;
  }>({
    dataKey: '',
    fileName: '',
  });
  const addFlag = useCallback(
    async ({
      dataKey,
      fileName,
      note,
      reason,
      problems,
      baseCategory,
      fileUpload,
      flagType,
      type,
      purposeType,
    }: FlagPayload) => {
      setLoading(true);
      const result = await saveFlag({
        id: queueId,
        flag: {
          dataKey,
          fileName,
          reason,
          problems,
          note,
          category: baseCategory,
          fileUpload,
          flagType,
          type,
          purposeType,
        },
      }).unwrap();
      setLoading(false);
      return result;
    },
    [queueId, saveFlag],
  );
  const showResolveModal = useCallback((dataKey: string, fileName?: string, id?: string) => {
    setFlagTarget({ dataKey, fileName: fileName || '', id });
    setIsResolveModalShowing(true);
  }, []);

  const hideResolveModal = useCallback(() => {
    setIsResolveModalShowing(false);
  }, []);

  const resolveFlag = useCallback(
    async (dataKey: string, status: string = FlagStatus.closed, fileName?: string, id?: string) => {
      setLoading(true);
      try {
        await resolveCurrentFlag({ id: queueId, dataKey, fileName, status, flagId: id }).unwrap();
      } catch (e: any) {
        setLoading(false);
        toast.error(
          'An error occurred while resolving the flag: ' + (e?.data?.message || e.message || 'Unknown error'),
        );
        throw e;
      }
      setLoading(false);
      hideResolveModal();
    },
    [queueId, resolveCurrentFlag, hideResolveModal],
  );

  const showModal = useCallback(
    (
      dataKey: string,
      flagType?: RequirementFlagSettingsType,
      fileName?: string,
      baseCategory?: string,
      type?: string,
    ) => {
      setFlagTarget({ dataKey, fileName: fileName || '', baseCategory, flagType, type });
      setIsModalShowing(true);
    },
    [],
  );

  const hideModal = useCallback(() => {
    setIsModalShowing(false);
    setIsEditMode(false);
  }, []);

  // @deprecated By getFlagsByFlagType
  const getFlag = useCallback(
    (dataKeys: string | string[], fileName?: string, includeResolved?: boolean) => {
      const keys = Array.isArray(dataKeys) ? dataKeys : [dataKeys];
      return queue?.flags?.find(
        (flag: Flag) =>
          (includeResolved || !flag.resolved) &&
          flag.dataKey != null &&
          keys.includes(flag.dataKey) &&
          (!fileName || flag.fileName === fileName) &&
          !flag.flagType,
      );
    },
    [queue?.flags],
  );

  const getFlagsByDocumentType = useCallback(
    (type: string) => {
      if (!queue?.flags) {
        return [];
      }

      return queue?.flags.filter(
        (flag: Flag) => (flag.type === type || flag.documentType === type) && !flag.resolved,
      );
    },
    [queue?.flags],
  );

  const getFlagsByFlagType = useCallback(
    (
      flagType: RequirementFlagSettingsType,
      includeResolved?: boolean,
      dataKeys?: string | string[],
      fileName?: string,
    ) => {
      if (!queue?.flags) {
        return [];
      }
      const keys = Array.isArray(dataKeys) ? dataKeys : [dataKeys];

      return queue?.flags.filter(
        (flag: Flag) =>
          (includeResolved || !flag.resolved) &&
          (!fileName || flag.fileName === fileName) &&
          (!dataKeys || keys.includes(flag.dataKey)) &&
          (flag.flagType === flagType || flag.type === flagType),
      );
    },
    [queue?.flags],
  );

  const getAllFlags = useCallback(
    (includeResolved?: boolean) => {
      if (!queue?.flags) {
        return [];
      }
      if (includeResolved) {
        return (
          queue?.flags.filter(
            (flag: Flag) => flag.status !== FlagStatus.closedDeleted && !flag.subjectArchivedOrVoided,
          ) || []
        );
      } else {
        return queue?.flags.filter(
          (flag: Flag) =>
            !flag.resolved && flag.status !== FlagStatus.closedDeleted && !flag.subjectArchivedOrVoided,
        );
      }
    },
    [queue?.flags],
  );

  const showEditModal = useCallback((flag: Flag) => {
    setIsEditMode(true);
    setFlagTarget({
      dataKey: flag.dataKey ?? '',
      fileName: flag.fileName || '',
      baseCategory: flag.category,
      type: flag.type,
      flagType: flag.flagType,
      id: flag.id?.toString(),
    });
    setIsModalShowing(true);
  }, []);

  const updateFlag = useCallback(
    async (flagId: string, flag: FlagPayload) => {
      setLoading(true);
      try {
        const result = await updateCurrentFlag({
          id: queueId,
          flagId,
          flag,
        }).unwrap();
        setLoading(false);
        return result;
      } catch (e) {
        setLoading(false);
        throw e;
      }
    },
    [queueId, updateCurrentFlag],
  );

  const showFlagIndicatorsToEpc = useCallback(() => {
    if (isFinCoUser) {
      return true;
    }

    const queueHasRejectionFlags = getAllFlags(false).some(
      (flag: Flag) => flag.purposeType === FlagPurposeType.rejection,
    );
    const queueIsNotReReview = queue?.status !== QueueStatus.reReview;
    const queueHasBeenReviewed = queue?.history?.some(
      (item: QueueHistory) =>
        item?.action === QueueHistoryAction.status && NON_SPECIAL_PACKAGE_REVIEWED_STATUSES.has(item?.changedTo),
    );
    const queueIsConditionallyApproved = queue?.status === QueueStatus.conditionallyApproved;

    return queueIsConditionallyApproved || (queueHasRejectionFlags && queueIsNotReReview && queueHasBeenReviewed);
  }, [queue, isFinCoUser, getAllFlags]);

  const getVisibleFlags = useCallback(
    (flags: Flag[]) => {
      if (!flagDrivenPackageReview) {
        return flags;
      }

      const shouldShowFlagIndicatorsToEpc = showFlagIndicatorsToEpc();

      return flags.filter((flag) => {
        const isAdditionalReviewFlag = flag.purposeType === FlagPurposeType.additionalReview;
        const isStipulationOrRejectionFlag = [FlagPurposeType.stipulation, FlagPurposeType.rejection].includes(
          flag.purposeType as FlagPurposeType,
        );
        const shouldHideAdditionalReviewFlag = !isFinCoUser && isAdditionalReviewFlag;
        const shouldHideStipulationOrRejectionFlag = isStipulationOrRejectionFlag && !shouldShowFlagIndicatorsToEpc;

        return !shouldHideAdditionalReviewFlag && !shouldHideStipulationOrRejectionFlag;
      });
    },
    [isFinCoUser, showFlagIndicatorsToEpc, flagDrivenPackageReview],
  );

  return (
    <Provider
      value={{
        isModalShowing,
        addFlag,
        updateFlag,
        resolveFlag,
        shouldHide: !queue?.id || !isFinCoUser,
        showModal,
        showEditModal,
        showResolveModal,
        hideModal,
        flags: queue?.flags || [],
        getFlag,
        getFlagsByDocumentType,
        getFlagsByFlagType,
        getAllFlags,
        isLoading,
        setLoading,
        queue,
        getVisibleFlags,
      }}
    >
      {flagDrivenPackageReview && flagTarget?.flagType ? (
        <NewAddFlagModal
          accountId={queue?.account?.id}
          isOpen={isModalShowing}
          onDismiss={hideModal}
          dataKey={flagTarget.dataKey}
          fileName={flagTarget.fileName}
          baseCategory={flagTarget.baseCategory}
          flagType={flagTarget.flagType}
          type={flagTarget.type}
          showUploadButton={isFinCoUser}
          editMode={isEditMode}
          flagId={flagTarget.id}
          queueStatus={queue?.status}
        />
      ) : (
        <AddFlagModal
          accountId={queue?.account?.id}
          isOpen={isModalShowing}
          onDismiss={hideModal}
          dataKey={flagTarget.dataKey}
          fileName={flagTarget.fileName}
          baseCategory={flagTarget.baseCategory}
          type={flagTarget.type}
          showUploadButton={isFinCoUser}
        />
      )}

      {isResolveModalShowing && (
        <ResolveFlagModal
          isOpen={isResolveModalShowing}
          onDismiss={hideResolveModal}
          onResolve={(status: string) => resolveFlag(flagTarget.dataKey, status, flagTarget.fileName, flagTarget.id)}
          dataKey={flagTarget.dataKey}
          fileName={flagTarget.fileName}
        />
      )}

      {children}
    </Provider>
  );
};

export { QueueFlagsProvider, QueueFlagsContext };
